import React, { useState, useCallback, useMemo, useEffect } from 'react'
import PropTypes from 'prop-types'
import { makeStyles, TextField, InputAdornment, CircularProgress } from '@material-ui/core'
import { TreeView, TreeItem } from '@material-ui/lab'
import Icon from '../../../../atoms/Icon'
import { dataCatalog } from '../../../../../api/coreData'
import Alert from '../../../../atoms/Alert'

const useStyles = makeStyles((theme) => ({
  utilityPanel: {
    width: '100%',
    height: '100%',
    backgroundColor: 'var(--surface-bg-color-d1)',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column'
  },
  header: {
    padding: '12px 16px',
    borderBottom: 'var(--border-neutral-thin)',
    fontWeight: 600,
    fontSize: 'var(--font-size-sm)'
  },
  searchBox: {
    padding: '8px 16px',
    borderBottom: '1px solid var(--color-neutral-600)',
    '& .MuiTextField-root': {
      width: '100%'
    },
    '& .MuiInputBase-root': {
      fontSize: '13px',
      backgroundColor: 'var(--surface-bg-color-d4)',
      '&:hover': {
        backgroundColor: 'var(--surface-bg-color-d5)'
      }
    },
    '& .MuiInputBase-input': {
      padding: '6px 12px'
    }
  },
  schemaTree: {
    flex: 1,
    overflow: 'auto',
    padding: '8px',
    fontSize: '13px',
    fontFamily: 'var(--font-family-monospace)',
    '& .MuiTreeItem-root': {
      '& > .MuiTreeItem-content': {
        padding: '2px var(--padding-xs)',
        '&:hover': {
          backgroundColor: 'color-mix(in srgb, var(--color-primary-500) 90%, transparent)',
          color: 'var(--color-contrast-primary-500)'
        }
      }
    },
    '& .MuiTreeItem-label': {
      fontSize: '13px',
      fontFamily: 'inherit'
    },
    '& .MuiTreeItem-iconContainer': {
      width: '16px',
      marginRight: '4px'
    }
  },
  treeItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '2px 0',
    cursor: 'pointer',
    userSelect: 'none'
  },
  schemaIcon: {
    fontSize: '14px',
    color: 'var(--color-warning-500)'
  },
  tableIcon: {
    fontSize: '14px',
    color: 'var(--color-info-500)'
  },
  columnIcon: {
    fontSize: '12px',
    color: 'var(--font-color-main)'
  },
  columnType: {
    paddingInline: 'var(--padding-xs)',
    backgroundColor: 'var(--color-neutral-300)',
    borderRadius: 'var(--border-radius-sm)',
    color: 'var(--color-contrast-neutral-300)',
    marginLeft: 'auto',
    fontSize: '11px',
    fontFamily: 'var(--font-family-monospace)'
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    flexDirection: 'column',
    gap: '16px'
  },
  errorContainer: {
    padding: '16px'
  },
  viewDescription: {
    fontSize: 'var(--font-size-xs)',
    fontStyle: 'italic',
    marginLeft: '8px'
  },
  emptyState: {
    padding: 'var(--padding-xl)',
    textAlign: 'center',
    color: 'var(--color-neutral-400)',
    fontSize: 'var(--font-size-sm)'
  }
}))

function UtilityPanel ({ onInsertText }) {
  const classes = useStyles()
  const [expanded, setExpanded] = useState([])
  const [searchTerm, setSearchTerm] = useState('')

  // Fetch schema data from API
  const { data: schemaResponse, isLoading, error } = dataCatalog.useListCatalogSchema({
    includeUnpublished: false
  })

  // Transform API data to component format
  const schemaData = useMemo(() => {
    if (!schemaResponse?.catalogViews) return []

    const groupedBySchema = schemaResponse.catalogViews.reduce((acc, datasource) => {
      const schema = datasource.schema.code || 'unknown'
      if (!acc[schema]) {
        acc[schema] = []
      }
      acc[schema].push(datasource)
      return acc
    }, {})

    return Object.entries(groupedBySchema).map(([schemaCode, datasources]) => ({
      id: schemaCode,
      name: schemaCode,
      type: 'schema',
      tables: datasources.map(datasource => ({
        id: `${schemaCode}.${datasource.code}`,
        name: datasource.displayName,
        code: datasource.code,
        type: 'table',
        description: datasource.description,
        // Extract columns from configuration
        columns: (datasource.datapoints || [])
          .sort((a, b) => (a.ordinal || 999) - (b.ordinal || 999))
          .map(datapoint => ({
            id: `${schemaCode}.${datasource.code}.${datapoint.accessor}`,
            name: datapoint.accessor,
            type: 'column',
            dataType: datapoint.type?.toUpperCase() || 'UNKNOWN',
            title: datapoint.displayName,
            description: datapoint.description,
            pii: datapoint.isPii || false,
            ai: datapoint.isAiEnabled || false,
            deprecated: datapoint.isDeprecated || false
          }))
      }))
    }))
  }, [schemaResponse])

  // Auto-expand all schemas when data loads
  useEffect(() => {
    if (schemaData.length > 0 && expanded.length === 0) {
      setExpanded(schemaData.map(schema => schema.id))
    }
  }, [schemaData, expanded.length])

  const handleToggle = useCallback((event, nodeIds) => {
    setExpanded(nodeIds)
  }, [])

  const handleItemClick = useCallback((event, item) => {
    event.stopPropagation()
    if (!onInsertText) return

    if (item.type === 'table' && item.code) {
      // Insert code for tables
      onInsertText(item.code)
    } else if (item.type === 'column' && item.name) {
      // Insert column name
      onInsertText(item.name)
    }
  }, [onInsertText])

  const handleSearchChange = useCallback((event) => {
    const term = event.target.value.toLowerCase()
    setSearchTerm(term)

    // Auto-expand nodes when searching
    if (term) {
      const expandedNodes = []
      schemaData.forEach(schema => {
        // Check if schema or any of its children match
        const schemaMatches = schema.name.toLowerCase().includes(term)
        const hasMatchingTable = schema.tables?.some(table =>
          table.name.toLowerCase().includes(term) ||
          table.code?.toLowerCase().includes(term) ||
          table.description?.toLowerCase().includes(term) ||
          table.columns?.some(col =>
            col.name.toLowerCase().includes(term) ||
            col.title?.toLowerCase().includes(term) ||
            col.description?.toLowerCase().includes(term)
          )
        )

        if (schemaMatches || hasMatchingTable) {
          expandedNodes.push(schema.id)
          schema.tables?.forEach(table => {
            const tableMatches = table.name.toLowerCase().includes(term) ||
                                table.code?.toLowerCase().includes(term) ||
                                table.description?.toLowerCase().includes(term)
            const hasMatchingColumn = table.columns?.some(col =>
              col.name.toLowerCase().includes(term) ||
              col.title?.toLowerCase().includes(term) ||
              col.description?.toLowerCase().includes(term)
            )
            if (tableMatches || hasMatchingColumn) {
              expandedNodes.push(table.id)
            }
          })
        }
      })
      setExpanded(expandedNodes)
    } else {
      // Reset to expand all schemas when search is cleared
      setExpanded(schemaData.map(schema => schema.id))
    }
  }, [schemaData])

  const filterNodes = (nodes) => {
    if (!searchTerm) return true

    const term = searchTerm.toLowerCase()

    // Check node name
    if (nodes.name?.toLowerCase().includes(term)) return true

    // Check table-specific fields
    if (nodes.type === 'table') {
      if (nodes.code?.toLowerCase().includes(term)) return true
      if (nodes.description?.toLowerCase().includes(term)) return true
    }

    // Check column-specific fields
    if (nodes.type === 'column') {
      if (nodes.title?.toLowerCase().includes(term)) return true
      if (nodes.description?.toLowerCase().includes(term)) return true
      if (nodes.dataType?.toLowerCase().includes(term)) return true
    }

    // Check child tables
    if (nodes.tables) {
      return nodes.tables.some(table => filterNodes(table))
    }

    // Check child columns
    if (nodes.columns) {
      return nodes.columns.some(column => filterNodes(column))
    }

    return false
  }

  const renderTree = (nodes) => {
    // Handle column nodes
    if (nodes.type === 'column') {
      if (searchTerm && !filterNodes(nodes)) {
        return null
      }

      return (
        <TreeItem
          key={nodes.id}
          nodeId={nodes.id}
          label={
            <div
              className={classes.treeItem}
              onDoubleClick={(e) => handleItemClick(e, nodes)}
              title={nodes.description || nodes.title || 'Double-click to insert'}
            >
              <span>{nodes.name}</span>
              {nodes.pii && <span style={{ color: 'var(--color-warning-500)', marginLeft: '4px' }}>(PII)</span>}
              <span className={classes.columnType}>{nodes.dataType}</span>
            </div>
          }
        />
      )
    }

    // Handle schema and table nodes
    const icon = nodes.type === 'schema'
      ? <Icon name='folder' className={classes.schemaIcon} />
      : <Icon name='file' className={classes.tableIcon} />

    const children = nodes.type === 'table'
      ? nodes.columns?.filter(column => filterNodes(column))
      : nodes.tables?.filter(table => filterNodes(table))

    if (searchTerm && children?.length === 0 && !filterNodes(nodes)) {
      return null
    }

    return (
      <TreeItem
        key={nodes.id}
        nodeId={nodes.id}
        label={
          <div
            className={classes.treeItem}
            onDoubleClick={(e) => nodes.type === 'table' ? handleItemClick(e, nodes) : undefined}
            title={nodes.type === 'table' ? (nodes.description || 'Double-click to insert') : ''}
          >
            {icon}
            <span>{nodes.type === 'schema' ? nodes.name : nodes.code}</span>
            {nodes.type === 'table' && nodes.description && (
              <span className={classes.viewDescription} title={nodes.description}>
                {nodes.name.length > 50
                  ? nodes.name.substring(0, 50) + '...'
                  : nodes.name}
              </span>
            )}
          </div>
        }
      >
        {children?.map((child) => renderTree(child))}
      </TreeItem>
    )
  }

  const filteredSchemas = schemaData.filter(schema => filterNodes(schema))

  if (isLoading) {
    return (
      <div className={classes.utilityPanel}>
        <div className={classes.header}>Schema Browser</div>
        <div className={classes.loadingContainer}>
          <CircularProgress size={40} />
          <span>Loading schema...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={classes.utilityPanel}>
        <div className={classes.header}>Schema Browser</div>
        <div className={classes.errorContainer}>
          <Alert severity='error' title='Failed to load schema'>
            {error.message || 'Unable to fetch catalog schema'}
          </Alert>
        </div>
      </div>
    )
  }

  return (
    <div className={classes.utilityPanel}>
      <div className={classes.header}>
        Schema Browser
      </div>
      <div className={classes.searchBox}>
        <TextField
          placeholder='Search views, columns...'
          value={searchTerm}
          onChange={handleSearchChange}
          variant='outlined'
          size='small'
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <Icon name='search' style={{ fontSize: '16px' }} />
              </InputAdornment>
            )
          }}
        />
      </div>
      {filteredSchemas.length === 0 ? (
        <div className={classes.emptyState}>
          {searchTerm ? 'No views found matching your search' : 'No catalog views available'}
        </div>
      ) : (
        <TreeView
          className={classes.schemaTree}
          defaultCollapseIcon={<Icon name='chevronDown' />}
          defaultExpandIcon={<Icon name='chevronRight' />}
          expanded={expanded}
          onNodeToggle={handleToggle}
        >
          {filteredSchemas.map((schema) => renderTree(schema))}
        </TreeView>
      )}
    </div>
  )
}

UtilityPanel.propTypes = {
  onInsertText: PropTypes.func
}

export default UtilityPanel
